import Vue from 'vue'
import consts from '@/consts'
import auth from '@/auth'
import {createActionHelpers} from 'vuex-loading'
import i18n from '@/translator'

const {startLoading, endLoading} = createActionHelpers({
  moduleName: 'loading'
})

const initialStates = function () {
  return {
    isEmailSent: false,
    user: null,
    loading: false,
    error: '',
    input: '',
    validVoucher: false,
    isProducer: false,
    isSubscribed: false,
    links: Object,
    myFavorites: [],
    myTracks: [],
    myPlaylists: [],
    myVotes: null,
    myShopping: [],
    preventLogout: false,
    userPromise: null,
    oauthLogin: false,
    followedArtists: [],
    myLikedPlaylists: [],
    referredCode: null,
    subsReferredCode: null
  }
}
const mutations = {
  loading (state, value) {
    state.loading = value
  },
  error (state, value) {
    state.error = value.message
    state.input = value.input
  },
  resetError (state) {
    state.error = ''
  },
  logout (state) {
    let init = initialStates()
    Object.keys(init).forEach((key) => {
      state[key] = init[key]
    })
    auth.removeToken()
    auth.removeMsisdn()
    auth.removeProvider()
  },
  setOauthLogin (state, value) {
    state.oauthLogin = value
  },
  setUser (state, user) {
    state.user = user
  },
  setReferredCode (state, referredCode) {
    state.referredCode = referredCode
  },
  setSubsReferredCode (state, subsReferredCode) {
    state.subsReferredCode = subsReferredCode
  },
  forgotPass (state, value) {
    state.isEmailSent = value
  },
  validVoucher (state, value) {
    state.validVoucher = value
  },
  isProducer (state, value) {
    state.isProducer = value
  },
  links (state, value) {
    state.links = value
  },
  isSubscribed (state, value) {
    state.isSubscribed = value
  },
  setMyFavorites (state, value) {
    state.myFavorites = value
  },
  setMyTracks (state, value) {
    state.myTracks = value
  },
  setMyPlaylists (state, value) {
    state.myPlaylists = value
  },
  setMyVotes (state, value) {
    state.myVotes = value
  },
  setMyShopping (state, value) {
    state.myShopping = value
  },
  decreaseMyVotes (state) {
    state.myVotes--
  },
  updateProfileImage (state, value) {
    state.user.picture = value.path + value.filename
  },
  setPreventLogout (state, value) {
    state.preventLogout = value
  },
  setUserPromise (state, value) {
    state.userPromise = value
  },
  setFollowing (state, value) {
    state.followedArtists = value
  },
  setMyLikedPlaylists (state, value) {
    state.myLikedPlaylists = value
  }
}

const actions = {
  subscribeByPaypal ({commit, dispatch, state}, subsData) {
    startLoading(dispatch, 'subscribeByPaypal')
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/payment/createSubscriptionByPayPal', {
        data: subsData
      }).then(
        function (response) {
          endLoading(dispatch, 'subscribeByPaypal')
          if (response.ok === true) {
            commit('isSubscribed', (response.ok === true))
            this.$toasted.show('Paypal Payment Subscription Success', {type: 'success'})
            resolve()
          } else {
            commit('isSubscribed', false)
            commit('error', {error: true, message: i18n.t('error.store.network')})
            reject(new Error(i18n.t('error.store.network')))
          }
        },
        function () {
          endLoading(dispatch, 'subscribeByPaypal')
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },
  paypalCaptureOrder ({commit, dispatch, state}, orderId) {
    startLoading(dispatch, 'paypalCaptureOrder')
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/payment/paypalCaptureOrder', {
        order_id: orderId
      }).then(
        function (response) {
          endLoading(dispatch, 'paypalCaptureOrder')
          if (response.body.status === 'ok') {
            resolve(response.body.data)
          } else {
            commit('error', {error: true, message: i18n.t('error.store.network')})
            reject(new Error(i18n.t('error.store.network')))
          }
        },
        function () {
          endLoading(dispatch, 'paypalCaptureOrder')
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },
  setUser ({commit, dispatch, state}, user) {
    commit('setUser', user)
    commit('isProducer', user.user_type === consts.PRODUCER_TYPE)
    endLoading(dispatch, 'user login')
    if (user.subscription) {
      commit('isSubscribed', user.subscription.active)
      return Promise.resolve()
    } else {
      return dispatch('checkSubscription')
    }
  },
  loginAuth ({commit, dispatch, state}, oAuthUser) {
    startLoading(dispatch, 'user login')
    let request = Vue.http.post(consts.API_BASE_URL + `/oauth/login`, {
      user: oAuthUser
    })
    request.then((response) => {
      if (response.body.status) {
        auth.setToken(response.body.token)
        return dispatch('setUser', response.body.user)
      } else {
        commit('error', {
          message: response.body.error.message,
          input: ''
        })
        endLoading(dispatch, 'user login')
        return Promise.reject(new Error(response.body.error.message))
      }
    }).catch(() => {
      endLoading(dispatch, 'user login')
    })
    commit('setUserPromise', request)
    request.then(() => {
      dispatch('getMyVotes')
      endLoading(dispatch, 'user login')
    })
    return request
  },
  login ({commit, dispatch, state}, user) {
    startLoading(dispatch, 'user login')
    const promise = Vue.http.post(consts.API_BASE_URL + '/users/login', {
      email: user.email,
      password: user.pass
    }).then((response) => {
      if (response.body.status) {
        auth.setToken(response.body.token)
        return dispatch('setUser', response.body.user)
      } else {
        commit('error', {
          message: response.body.error.message,
          input: ''
        })
        return Promise.reject(new Error(response.body.error.message))
      }
    }).catch((e) => {
      endLoading(dispatch, 'user login')
      let message = e.message ? e.message : i18n.t('error.store.network')
      commit('error', {
        message: message,
        input: ''
      })
      return Promise.reject(new Error(message))
    })
    commit('setUserPromise', promise)
    promise.then(() => {
      if (state.isSubscribed) {
        dispatch('getMyVotes')
      }
      endLoading(dispatch, 'user login')
    })
    return promise
  },
  loginByMsisdn ({commit, dispatch, state}, { msisdn, provider = null, country = null, enc = false, isValidation = false }) {
    startLoading(dispatch, 'user login')
    msisdn = msisdn.replace(/\s/g, '+')
    var url = consts.API_BASE_URL + '/subs/login'

    if (provider !== null) {
      url += '/' + provider
    } else {
      url += '/bitel'
      country = 'per'
    }

    url += '/' + msisdn

    var data = {
      'enc': enc,
      'country': country
    }

    const promise = Vue.http.post(url, data).then((response) => {
      if (response.body.status) {
        auth.setToken(response.body.token)
        if (response.body.msisdn) {
          auth.setMsisdn(response.body.msisdn)
          auth.setProvider(response.body.provider)
        }
        if (!isValidation) {
          window.location.href = '/home'
        }
        return dispatch('setUser', response.body.user)
      } else {
        commit('logout')
      }
      if (!isValidation) {
        window.location.href = '/home'
      }
    }).catch((e) => {
      return {
        message: e
      }
    })
    commit('setUserPromise', promise)
    promise.then(() => {
      if (state.isSubscribed) {
        dispatch('getMyVotes')
      }
      endLoading(dispatch, 'user login')
    })
    return promise
  },
  autologinRecurrent ({commit, dispatch, state}, { iterations, interval, msisdn, provider = null, country = null, enc = false, route = '/' }) {
    startLoading(dispatch, 'user login')
    msisdn = msisdn.replace(/\s/g, '+')
    var url = consts.API_BASE_URL + '/subs/login'

    if (provider !== null) {
      url += '/' + provider
    } else {
      url += '/bitel'
      country = 'per'
    }

    if (provider === 'entel') {
      country = 'per'
    }

    if (country !== null) {
      // url += '/' + country
    }

    url += '/' + msisdn

    var data = enc ? { 'enc': true } : {}
    var count = 1
    // localStorage.removeItem('token')
    setInterval(() => {
      if (count > iterations) {
        window.location.href = '/'
      } else {
        const promise = Vue.http.post(url, data)
          .then((response) => {
            if (response.body.status) {
              auth.setToken(response.body.token)
              if (response.body.msisdn) {
                auth.setMsisdn(response.body.msisdn)
                auth.setProvider(response.body.provider)
              }
              window.location.href = route
              return dispatch('setUser', response.body.user)
            } else {
              count++
            }
          }).catch((e) => {
            count++
            return {
              message: e
            }
          })
        commit('setUserPromise', promise)
        promise.then(() => {
          if (state.isSubscribed) {
            dispatch('getMyVotes')
          }
          endLoading(dispatch, 'user login')
        })
        return promise
      }
    }, interval * 1000)
  },
  autologinRecurrentEntel ({commit, dispatch, state}, { iterations, interval, msisdn, provider = null, enc = false, route = '/' }) {
    startLoading(dispatch, 'user login')
    msisdn = msisdn.replace(/\s/g, '+')
    var url = consts.API_BASE_URL + '/subs/loginProvider'

    if (provider !== null) {
      url += '/' + provider
    }

    url += '/' + msisdn

    var data = enc ? { 'enc': true } : {}
    var count = 1
    // localStorage.removeItem('token')
    setInterval(() => {
      if (count > iterations) {
        window.location.href = '/'
      } else {
        const promise = Vue.http.post(url, data)
          .then((response) => {
            if (response.body.status) {
              auth.setToken(response.body.token)
              if (response.body.msisdn) {
                auth.setMsisdn(response.body.msisdn)
                auth.setProvider(response.body.provider)
              }
              window.location.href = route
              return dispatch('setUser', response.body.user)
            } else {
              count++
            }
          }).catch((e) => {
            count++
            return {
              message: e
            }
          })
        commit('setUserPromise', promise)
        promise.then(() => {
          if (state.isSubscribed) {
            dispatch('getMyVotes')
          }
          endLoading(dispatch, 'user login')
        })
        return promise
      }
    }, interval * 1000)
  },
  checkUser ({commit, dispatch, state}) {
    startLoading(dispatch, 'user checkUser')
    const promise = Vue.http.get(consts.API_BASE_URL + '/users/getMyData').then(
      function (res) {
        if (res.body.status) {
          return dispatch('setUser', res.body.data)
        } else {
          commit('logout')
          commit('error', {error: true, message: i18n.t('error.store.userValidation')})
          return Promise.reject(new Error(i18n.t('error.store.userValidation')))
        }
      },
      function () {
        commit('logout')
        commit('error', {error: true, message: i18n.t('error.store.network')})
        endLoading(dispatch, 'user checkUser')
        return Promise.reject(new Error(i18n.t('error.store.network')))
      })
    commit('setUserPromise', promise)
    promise.then(() => {
      dispatch('getMyVotes')
      endLoading(dispatch, 'user checkUser')
    })
    return promise
  },

  checkSubscription ({commit}) {
    return Vue.http.get(consts.API_BASE_URL + '/users/check').then(
      function (res) {
        commit('isSubscribed', (res.body.status === 'ok'))
      },
      function () {
        commit('error', {error: true, message: i18n.t('error.store.network')})
        return Promise.reject(new Error(i18n.t('error.store.network')))
      })
  },

  cancelBeatheySuscription ({dispatch}) {
    startLoading(dispatch, 'user cancelBeatheySuscription')
    return Vue.http.get(consts.API_BASE_URL + '/payment/cancelBeatheySuscription').then((response) => {
      if (response.body.status) {
        endLoading(dispatch, 'user cancelBeatheySuscription')
        return Promise.resolve(response.body.preapproval_suscription.response.status)
      } else {
        return Promise.reject(new Error(response.body.error.message))
      }
    }).catch((e) => {
      endLoading(dispatch, 'user cancelBeatheySuscription')
      return Promise.resolve(e.body.error.message)
      // return Promise.reject(new Error(e.body.error.message ? e.body.error.message : i18n.t('error.store.network')))
    })
  },

  deleteBeatheyAccount ({dispatch}) {
    startLoading(dispatch, 'user deleteBeatheyAccount')
    return Vue.http.delete(consts.API_BASE_URL + '/users/delete').then((response) => {
      if (response.body.status) {
        endLoading(dispatch, 'user deleteBeatheyAccount')
        return Promise.resolve(response.body.status)
      } else {
        return Promise.resolve(response.body.status)
      }
    }).catch((e) => {
      endLoading(dispatch, 'user deleteBeatheyAccount')
      return Promise.resolve(e.body.error.message)
    })
  },

  checkSubscriptionGateway ({commit, dispatch}) {
    return Vue.http.get(consts.API_BASE_URL + '/users/getSubscriptionGateway').then((response) => {
      if (response.body.status) {
        endLoading(dispatch, 'user checkSubscriptionGateway')
        return Promise.resolve(response.body.data)
      } else {
        return Promise.reject(new Error(response.body.error.message))
      }
    }).catch((e) => {
      endLoading(dispatch, 'user checkSubscriptionGateway')
      return Promise.reject(new Error(e.message ? e.message : i18n.t('error.store.network')))
    })
  },

  logout ({commit}) {
    // Vue.http.post(consts.API_BASE_URL + '/users/logout')
    commit('logout')
  },

  forgotPass ({commit}, email) {
    commit('loading', true)
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/users/forgot', {
        email: email
      }).then(
        function (response) {
          commit('loading', false)
          if (response.body.status === 'ok') {
            commit('forgotPass', true)
            resolve()
          } else {
            commit('error', {error: true, message: i18n.t('error.emailInvalid')})
            reject(new Error(i18n.t('error.emailInvalid')))
          }
        },
        function () {
          commit('loading', false)
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  register ({commit, dispatch}, user) {
    commit('loading', true)
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/users/register', {
        user_name: user.username,
        password: user.pass,
        dni: user.dni,
        telefono: user.phone,
        email: user.email,
        direccion: user.address,
        birth: user.birthdate,
        user_type: user.userType,
        voucher: user.voucher
      }).then(
        function (response) {
          commit('loading', false)
          if (response.body.status === 'ok') {
            dispatch('login', {email: user.email, pass: user.pass})
            resolve()
          } else {
            if (response.body.status === 'error') {
              switch (response.body.error) {
                case 'user name exist': {
                  commit('error', {
                    message: i18n.t('error.usernameRepeated'),
                    input: 'username'
                  })
                  break
                }
                case 'email exist': {
                  commit('error', {
                    message: i18n.t('error.emailRepeated'),
                    input: 'email'
                  })
                  break
                }
                case 'the address is mandatory': {
                  commit('error', {
                    message: i18n.t('error.addressRequired'),
                    input: 'address'
                  })
                  break
                }
                case 'the dni is mandatory': {
                  commit('error', {
                    message: i18n.t('error.idRequired'),
                    input: 'dni'
                  })
                  break
                }
                default: {
                  commit('error', {
                    message: i18n.t('error.registration'),
                    input: ''
                  })
                }
              }
              reject(new Error(response.body.error))
            }
          }
        },
        function () {
          commit('loading', false)
          commit('error', {
            error: true,
            message: i18n.t('error.store.network')
          })
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  resetError ({commit}) {
    commit('resetError')
  },

  checkVoucher ({commit}, value) {
    return new Promise((resolve, reject) => {
      if (value !== '') {
        Vue.http.get(consts.API_BASE_URL + '/users/checkVouchers' + '?code=' + value).then(
          function (response) {
            if (response.body.status === 'ok') {
              commit('validVoucher', true)
              resolve(response.body.data)
            } else {
              if (response.body.status === 'error') {
                commit('validVoucher', false)
                commit('error', {
                  message: i18n.t('error.voucherInvalid'),
                  input: 'voucher'
                })
                resolve(response.body.message)
              }
            }
          },
          function () {
            commit('error', {
              error: true,
              message: i18n.t('error.store.network')
            })
            reject(new Error(i18n.t('error.store.network')))
          }
        )
      } else {
        commit('validVoucher', true)
        resolve()
      }
    })
  },
  getSubscriptionLink ({commit, dispatch}, value) {
    startLoading(dispatch, 'user getPaymentLink')
    let mk = localStorage.getItem('userFromLandingMK')
    let voucherCode = null
    let referredCode = null
    let emailNeeded = null

    if (value.voucher) {
      voucherCode = value.voucher.code
    }
    if (value.refCode) {
      referredCode = value.refCode
    }
    if (value.emailNeeded) {
      emailNeeded = value.emailNeeded
    }
    return Vue.http.get(consts.API_BASE_URL + value.paymentUrl + value.gateway + '?mk=' + mk + '&voucherCode=' + voucherCode + '&referredCode=' + referredCode + '&emailNeeded=' + emailNeeded).then(response => {
      endLoading(dispatch, 'user getSubscriptionLink')
      if (response.body.status) {
        return Promise.resolve(response.body.next_url)
      } else {
        return Promise.reject(response.body.error)
      }
    }).catch(e => {
      endLoading(dispatch, 'user getSubscriptionLink')
      let message = e.message ? e.message : i18n.t('error.store.network')
      commit('error', {
        message: message,
        input: ''
      })
      return Promise.reject(new Error(message))
    })
  },

  getMyFavorites ({commit}, {genreIdFilter = 0, textToFilter = null}) {
    commit('loading', true)
    return new Promise((resolve, reject) => {
      Vue.http.get(consts.API_BASE_URL + '/users/getMyLikes' + '?genreIdFilter=' + genreIdFilter + '&textToFilter=' + textToFilter).then(
        function (response) {
          commit('loading', false)
          if (response.body.status === 'ok') {
            commit('setMyFavorites', response.body.data)
            resolve(response.body.data)
          } else {
            commit('error', {error: true, message: i18n.t('error.store.myFavorites')})
            reject(new Error(i18n.t('error.store.myFavorites')))
          }
        },
        function () {
          commit('loading', false)
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  getMyTracks ({commit}) {
    commit('loading', true)
    return new Promise((resolve, reject) => {
      Vue.http.get(consts.API_BASE_URL + '/tracks/getMyTracks').then(
        function (response) {
          commit('loading', false)
          if (response.body.status === 'ok') {
            commit('setMyTracks', response.body.data[0])
            resolve()
          } else {
            commit('error', {error: true, message: i18n.t('error.store.myTracks')})
            reject(new Error(i18n.t('error.store.myTracks')))
          }
        },
        function () {
          commit('loading', false)
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  getMyPlaylists ({commit, dispatch}) {
    startLoading(dispatch, 'user getMyPlaylists')
    return new Promise((resolve, reject) => {
      Vue.http.get(consts.API_BASE_URL + '/users/getMyPlaylists').then(
        function (response) {
          endLoading(dispatch, 'user getMyPlaylists')
          if (response.body.status) {
            commit('setMyPlaylists', response.body.data)
            resolve()
          } else {
            commit('error', {error: true, message: i18n.t('error.store.myPlaylists')})
            reject(new Error(i18n.t('error.store.myPlaylists')))
          }
        },
        function () {
          endLoading(dispatch, 'user getMyPlaylists')
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  getMyVotes ({commit, dispatch, state}) {
    startLoading(dispatch, 'user getMyVotes')
    let routeString = state.isSubscribed ? '/users/getMyVotes' : '/users/getMyVotes?votes=1'
    return Vue.http.get(consts.API_BASE_URL + routeString).then(
      function (response) {
        endLoading(dispatch, 'user getMyVotes')
        if (response.body.status) {
          commit('setMyVotes', response.body.data)
        } else {
          commit('error', {error: true, message: i18n.t('error.store.voteValidation')})
          return Promise.reject(new Error(i18n.t('error.store.voteValidation')))
        }
      },
      function () {
        endLoading(dispatch, 'user getMyVotes')
        commit('error', {error: true, message: i18n.t('error.store.network')})
        return Promise.reject(new Error(i18n.t('error.store.network')))
      })
  },

  getMyShopping ({commit, dispatch}) {
    startLoading(dispatch, 'user getMyShopping')
    return Vue.http.get(consts.API_BASE_URL + '/tracks/getMyShopping').then(
      function (response) {
        endLoading(dispatch, 'user getMyShopping')
        if (response.body.status) {
          commit('setMyShopping', response.body.data)
        } else {
          commit('error', {error: true, message: i18n.t('error.store.voteValidation')})
          return Promise.reject(new Error(i18n.t('error.store.voteValidation')))
        }
      },
      function () {
        endLoading(dispatch, 'user getMyShopping')
        commit('error', {error: true, message: i18n.t('error.store.network')})
        return Promise.reject(new Error(i18n.t('error.store.network')))
      })
  },

  editUser ({commit, state, dispatch}, user) {
    commit('loading', true)
    var data
    if (user.password === '') {
      data = {
        bio: user.bio,
        user_name: user.username,
        first_name: user.name,
        last_name: user.surname,
        dni: user.dni,
        telefono: user.phone,
        direccion: user.address,
        user_type: user.userType,
        country: user.country,
        city: user.city,
        birth: user.birthdate,
        email: user.email,
        accept_news_letter: user.acceptNewsLetter,
        accept_share_info: user.acceptShareInfo
      }
    } else {
      data = {
        bio: user.bio,
        password: user.password,
        user_name: user.username,
        first_name: user.name,
        last_name: user.surname,
        dni: user.dni,
        telefono: user.phone,
        direccion: user.address,
        user_type: user.userType,
        country: user.country,
        city: user.city,
        birth: user.birthdate,
        email: user.email,
        accept_news_letter: user.acceptNewsLetter,
        accept_share_info: user.acceptShareInfo
      }
    }
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/users/editData', data).then(
        function (response) {
          commit('loading', false)
          if (response.body.status === 'ok' && response.body.data === 'saved') {
            dispatch('checkUser')
            resolve()
          } else {
            if (response.body.status === 'error') {
              switch (response.body.error) {
                case 'user name exist': {
                  commit('error', {
                    message: i18n.t('error.usernameRepeated'),
                    input: 'username'
                  })
                  break
                }
                case 'email exist': {
                  commit('error', {
                    message: i18n.t('error.emailRepeated'),
                    input: 'email'
                  })
                  break
                }
                default: {
                  commit('error', {
                    message: response.body.error,
                    input: ''
                  })
                }
              }
              reject(new Error(response.body.error))
            }
          }
        },
        function () {
          commit('loading', false)
          commit('error', {
            error: true,
            message: i18n.t('error.store.network')
          })
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  updateProfileImage ({commit}, value) {
    commit('updateProfileImage', value)
  },

  contact ({commit, dispatch}, value) {
    startLoading(dispatch, 'user contact')
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/contact', {
        email: value.email,
        name: value.name,
        message: value.message,
        provider: value.provider
      }).then(
        function (response) {
          endLoading(dispatch, 'user contact')
          if (response.body.status === true) {
            resolve()
          } else {
            commit('error', {error: true, message: i18n.t('error.store.contact')})
            reject(new Error(i18n.t('error.store.contact')))
          }
        },
        function () {
          endLoading(dispatch, 'user contact')
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  setPreventLogout ({commit}, value) {
    commit('setPreventLogout', value)
  },

  getMyFollowedArtists ({commit, dispatch}, {getIds = null, genreIdFilter = 0, textToFilter = null}) {
    startLoading(dispatch, 'user getMyFollowedArtists')
    return Vue.http.get(consts.API_BASE_URL + '/users/getMyFollowedArtists' + '?getIds=' + getIds + '&genreIdFilter=' + genreIdFilter + '&textToFilter=' + textToFilter).then(
      function (response) {
        endLoading(dispatch, 'user getMyFollowedArtists')
        if (response.body.status) {
          if (getIds) {
            localStorage.setItem('followedArtists', response.body.data)
          } else {
            // commit('setFollowing ', response.body.data, { root: true })
            return response.body.data
          }
        } else {
          commit('error', {error: true, message: i18n.t('error.store.network')})
          return Promise.reject(new Error(i18n.t('error.store.network')))
        }
      },
      function () {
        endLoading(dispatch, 'user getMyFollowedArtists')
        commit('error', {error: true, message: i18n.t('error.store.network')})
        return Promise.reject(new Error(i18n.t('error.store.network')))
      })
  },

  getMyLikedPlaylists ({commit, dispatch}, {genreIdFilter = 0, textToFilter = 'null'}) {
    startLoading(dispatch, 'user getMyLikedPlaylists')
    return new Promise((resolve, reject) => {
      Vue.http.get(consts.API_BASE_URL + '/playlists/searchLiked' + '?genreIdFilter=' + genreIdFilter + '&textToFilter=' + textToFilter).then(
        function (response) {
          endLoading(dispatch, 'user getMyLikedPlaylists')
          if (response.body.status === 'ok') {
            commit('setMyLikedPlaylists', response.body.data)
            resolve(response.body.data)
          } else {
            commit('error', {error: true, message: i18n.t('error.store.myFavorites')})
            reject(new Error(i18n.t('error.store.myFavorites')))
          }
        },
        function () {
          endLoading(dispatch, 'user getMyLikedPlaylists')
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  setFollowArtist ({commit, dispatch}, artistId) {
    commit('loading', true)
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/users/followArtist', {
        artist_id: artistId
      }).then(
        function (response) {
          commit('loading', false)
          if (response.body.status === true) {
            resolve(response.body.status)
          } else {
            commit('error', {error: true, message: i18n.t('error.store.network')})
            reject(new Error(i18n.t('error.store.network')))
          }
        },
        function () {
          commit('loading', false)
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },

  setReferredCodeAction ({commit}, value) {
    commit('setReferredCode', value)
  },

  setSubsReferredCodeAction ({commit}, value) {
    commit('setSubsReferredCode', value)
  },

  saveReferredCode ({commit, dispatch}, referredCode) {
    commit('loading', true)
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/users/saveReferredCode', {
        referredCode: referredCode
      }).then(
        function (response) {
          commit('loading', false)
          if (response.body.status === true) {
            commit('setReferredCode', response.body.data)
            resolve(response.body.data)
          } else {
            commit('error', {error: true, message: i18n.t('error.store.network')})
            reject(new Error(i18n.t('error.store.network')))
          }
        },
        function () {
          commit('loading', false)
          commit('error', {error: true, message: i18n.t('error.store.network')})
          reject(new Error(i18n.t('error.store.network')))
        })
    })
  },
  checkMyActiveUserReferredPlan ({commit, dispatch}) {
    startLoading(dispatch, 'user checkMyActiveUserReferredPlan')
    return Vue.http.get(consts.API_BASE_URL + '/users/checkMyActiveUserReferredPlan').then(
      function (response) {
        endLoading(dispatch, 'user checkMyActiveUserReferredPlan')
        if (response.body.status) {
          commit('setReferredCode', response.body.data)
          return response.body.data
        } else {
          commit('error', {error: true, message: i18n.t('error.store.network')})
          return Promise.reject(new Error(i18n.t('error.store.network')))
        }
      },
      function () {
        endLoading(dispatch, 'user checkMyActiveUserReferredPlan')
        commit('error', {error: true, message: i18n.t('error.store.network')})
        return Promise.reject(new Error(i18n.t('error.store.network')))
      })
  },
  editUserType ({dispatch}, userType) {
    startLoading(dispatch, 'user editUserType')
    return new Promise((resolve, reject) => {
      Vue.http.post(consts.API_BASE_URL + '/users/setUserType', userType).then((response) => {
        if (response.body.status === 'error') {
          reject(new Error(response.body.error))
        } else {
          resolve()
        }
        endLoading(dispatch, 'user editUserType')
      }).catch((e) => {
        endLoading(dispatch, 'user editUserType')
        return Promise.reject(new Error(e.message ? e.message : 'error editUserType'))
      })
    })
  },
  checkUserLogged ({commit}) {
    commit('loading', true)
    return new Promise((resolve, reject) => {
      Vue.http.get(consts.API_BASE_URL + '/users/getMyData').then(
        function (response) {
          commit('loading', false)
          if (response.body.status === true) {
            return resolve(response.body.data)
          } else {
            return reject(new Error(i18n.t('error.store.network')))
          }
        },
        function () {
          commit('loading', false)
          commit('error', {error: true, message: i18n.t('error.store.network')})
          return reject(new Error(i18n.t('error.store.network')))
        })
    })
  }
}

const getters = {
  isLoggedIn: state => {
    console.log(state.user)
    return state.user !== null
  },
  error: state => {
    return state.error
  },
  loading: state => {
    return state.loading
  },
  user: state => {
    return state.user
  },
  referredCode: state => {
    return state.referredCode
  },
  subsReferredCode: state => {
    return state.subsReferredCode
  },
  isEmailSent: state => {
    return state.isEmailSent
  },
  input: state => {
    return state.input
  },
  validVoucher: state => {
    return state.validVoucher
  },
  isProducer: state => {
    return state.isProducer
  },
  isSubscribed: state => {
    return state.isSubscribed
  },
  links: state => {
    return state.links
  },
  myFavorites: state => {
    return state.myFavorites
  },
  myTracks: state => {
    return state.myTracks
  },
  myPlaylists: state => {
    return state.myPlaylists
  },
  myVotes: state => {
    return state.myVotes
  },
  myShopping: state => {
    return state.myShopping
  },
  authToken: () => {
    return auth.getToken()
  },
  preventLogout: state => {
    return state.preventLogout
  },
  userPromise: state => {
    return state.userPromise
  },
  msisdn: () => {
    return auth.getMsisdn()
  },
  provider: () => {
    return auth.getProvider()
  },
  followedArtists: state => {
    return state.followedArtists
  },
  myLikedPlaylists: state => {
    return state.myLikedPlaylists
  }
}

const user = {
  namespaced: true,
  initialStates,
  state: initialStates(),
  mutations: mutations,
  actions: actions,
  getters: getters
}

export default user



// WEBPACK FOOTER //
// ./src/store/modules/user.js